<template>
    <div class="content-wrapper">
        <div class="row">
            <div class="col-sm-12 col-md-6">
                <app-breadcrumb :page-title="$t('employee_attendance_details')" :icon="'user-check'" />
            </div>
            <div class="col-sm-12 col-md-6 breadcrumb-side-button">
                <div class="float-md-right mb-3 mb-sm-3 mb-md-0 d-flex align-items-center">
                    <input
                        type="date"
                        v-model="selectedDate"
                        :placeholder="$t('select_date')"
                        @input="reloadTable"
                        class="form-control mr-2 d-inline-block attendance-date-input"
                    />
                    <div class="input-group mr-2 attendance-search-group">
                        <div class="input-group-prepend">
                            <span class="input-group-text">
                                <i class="fas fa-search"></i>
                            </span>
                        </div>
                        <input
                            type="text"
                            v-model="searchQuery"
                            :placeholder="$t('search_employee')"
                            @input="filterEmployees"
                            class="form-control"
                        />
                        <div class="input-group-append" v-if="searchQuery">
                            <button 
                                class="btn btn-outline-secondary" 
                                type="button" 
                                @click="clearSearch"
                                :title="$t('clear_search')"
                            >
                                <i class="fas fa-times"></i>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Statistics Cards -->
        <div class="row mb-4">
            <div class="col-md-3">
                <div class="card attendance-stat-card attendance-stat-card--cyan text-white">
                    <div class="card-body text-center">
                        <h3 class="mb-1">{{ formatCountValue(statistics.present_count || 0) }}</h3>
                        <p class="mb-0">{{ $t('present_employees') }}</p>
                    </div>
                </div>
            </div>
            <div class="col-md-3">
                <div class="card attendance-stat-card attendance-stat-card--dark text-white">
                    <div class="card-body text-center">
                        <h3 class="mb-1">{{ formatCountValue(statistics.absent_count || 0) }}</h3>
                        <p class="mb-0">{{ $t('absent_employees') }}</p>
                    </div>
                </div>
            </div>
            <div class="col-md-3">
                <div class="card attendance-stat-card attendance-stat-card--indigo text-white">
                    <div class="card-body text-center">
                        <h3 class="mb-1">{{ formatCountValue(statistics.late_count || 0) }}</h3>
                        <p class="mb-0">{{ $t('late_employees') }}</p>
                    </div>
                </div>
            </div>
            <div class="col-md-3">
                <div class="card attendance-stat-card attendance-stat-card--brand text-white">
                    <div class="card-body text-center">
                        <h3 class="mb-1">{{ formatCountValue(statistics.overtime_count || 0) }}</h3>
                        <p class="mb-0">{{ $t('overtime_employees') }}</p>
                    </div>
                </div>
            </div>
        </div>

        <!-- Grid View -->
        <div v-if="viewMode === 'grid'" class="row">
            <div class="col-lg-4 col-md-6 mb-4" v-for="employee in filteredEmployeeDetails" :key="employee.id">
                <div class="card employee-card h-100" :class="getCardClass(employee.status)">
                    <div class="card-body d-flex flex-column">
                        <div class="d-flex align-items-center mb-3">
                            <div class="employee-avatar mr-3">
                                <img 
                                    :src="employee.photo_url || employee.photo || getDefaultPhoto()" 
                                    :alt="employee.full_name"
                                    class="rounded-circle employee-photo"
                                    loading="lazy"
                                    decoding="async"
                                    width="100" 
                                    height="100"
                                >
                            </div>
                            <div class="flex-grow-1">
                                <h6 class="mb-1 employee-name">{{ employee.full_name }}</h6>
                                <p class="badge" :class="getStatusBadgeClass(employee.status)">
                                    {{ $t(employee.status) }}
                                </p>
                                <p>
                                    <strong>{{ employee.matricule }}</strong>
                                </p>
                            </div>
                        </div>
                        
                        <div class="employee-details flex-grow-1">
                            <div class="row mb-2">
                                <div class="col-6">
                                    <small class="text-muted">{{ $t('department') }}</small><br>
                                    <strong class="department-text">{{ employee.department }}</strong>
                                </div>
                                <div class="col-6">
                                    <small class="text-muted">{{ $t('shift') }}</small><br>
                                    <strong class="shift-text">{{ employee.shift }}</strong>
                                </div>
                            </div>

                            <!-- Contact and Location Info -->
                            <div class="row mb-2">
                                <div class="col-6" v-if="employee.phone">
                                    <small class="text-muted">{{ $t('phone') }}</small><br>
                                    <a :href="'tel:' + employee.phone" class="text-primary contact-link">
                                        <i class="fas fa-phone mr-1"></i>
                                        {{ employee.phone }}
                                    </a>
                                </div>
                                <div class="col-6" v-if="employee.location">
                                    <small class="text-muted">{{ $t('location') }}</small><br>
                                    <span class="text-info location-text">
                                        <i class="fas fa-map-marker-alt mr-1"></i>
                                        {{ employee.location }}
                                    </span>
                                </div>
                            </div>

                            <div class="row mb-2" v-if="employee.email">
                                <div class="col-12">
                                    <small class="text-muted">{{ $t('email') }}</small><br>
                                    <a :href="'mailto:' + employee.email" class="text-primary contact-link email-link">
                                        <i class="fas fa-envelope mr-1"></i>
                                        {{ employee.email }}
                                    </a>
                                </div>
                            </div>
                            
                            <div class="row mb-2" v-if="employee.first_check_in || employee.last_check_out">
                                <div class="col-6">
                                    <small class="text-muted">{{ $t('check_in') }}</small><br>
                                    <span class="text-success time-text">
                                        <i class="fas fa-sign-in-alt mr-1"></i>
                                        {{ employee.first_check_in || '-' }}
                                    </span>
                                </div>
                                <div class="col-6">
                                    <small class="text-muted">{{ $t('check_out') }}</small><br>
                                    <span class="text-danger time-text">
                                        <i class="fas fa-sign-out-alt mr-1"></i>
                                        {{ employee.last_check_out || '-' }}
                                    </span>
                                </div>
                            </div>
                            
                            <div class="row mb-3">
                                <div class="col-6">
                                    <small class="text-muted">{{ $t('total_hours') }}</small><br>
                                    <strong class="hours-text">{{ employee.total_hours }}h</strong>
                                </div>
                                <div class="col-6" v-if="employee.overtime_minutes > 0">
                                    <small class="text-muted">{{ $t('overtime') }}</small><br>
                                    <span class="text-info overtime-text">
                                        <i class="fas fa-clock mr-1"></i>
                                        {{ Math.round(employee.overtime_minutes/60) }}h
                                    </span>
                                </div>
                            </div>
                        </div>
                        
                        <!-- Footer section with fixed height -->
                        <div class="card-footer-section mt-auto">
                            <!-- Indicators -->
                            <div class="mb-2 indicators-section attendance-indicators-section">
                                <span v-if="employee.is_late" class="badge badge-warning mr-1">
                                    <i class="fas fa-exclamation-triangle mr-1"></i>{{ $t('late') }}
                                </span>
                                <span v-if="employee.is_early_departure" class="badge badge-info">
                                    <i class="fas fa-door-open mr-1"></i>{{ $t('early_departure') }}
                                </span>
                            </div>
                            
                            <!-- Check-in/out counts -->
                            <div class="d-flex justify-content-between">
                                <small class="text-muted">
                                    <i class="fas fa-arrow-right text-success"></i> {{ employee.total_checkins }} {{ $t('checkins') }}
                                </small>
                                <small class="text-muted">
                                    <i class="fas fa-arrow-left text-danger"></i> {{ employee.total_checkouts }} {{ $t('checkouts') }}
                                </small>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Table View -->
        <div v-if="viewMode === 'table'">
            <app-table 
                :id="tableId" 
                :options="options" 
                @action="getListAction" 
            />
        </div>

        <!-- Employee Detail Modal -->
        <div class="modal fade" id="employee-detail-modal" tabindex="-1">
            <div class="modal-dialog modal-lg">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">{{ $t('employee_attendance_detail') }}</h5>
                        <button type="button" class="close" data-dismiss="modal">
                            <span>&times;</span>
                        </button>
                    </div>
                    <div class="modal-body" v-if="selectedEmployee">
                        <!-- Detailed employee information -->
                        <div class="row">
                            <div class="col-md-4 text-center">
                                <img 
                                    :src="selectedEmployee.photo_url || selectedEmployee.photo || getDefaultPhoto()" 
                                    :alt="selectedEmployee.full_name"
                                    class="rounded-circle mb-3"
                                    loading="lazy"
                                    decoding="async"
                                    width="100" 
                                    height="100"
                                >
                                <h5>{{ selectedEmployee.full_name }}</h5>
                                <p class="text-muted">{{ selectedEmployee.matricule }}</p>
                            </div>
                            <div class="col-md-8">
                                <table class="table table-borderless">
                                    <tr>
                                        <td><strong>{{ $t('department') }}:</strong></td>
                                        <td>{{ selectedEmployee.department }}</td>
                                    </tr>
                                    <tr>
                                        <td><strong>{{ $t('shift') }}:</strong></td>
                                        <td>{{ selectedEmployee.shift }}</td>
                                    </tr>
                                    <tr>
                                        <td><strong>{{ $t('status') }}:</strong></td>
                                        <td>
                                            <span class="badge" :class="getStatusBadgeClass(selectedEmployee.status)">
                                                {{ $t(selectedEmployee.status) }}
                                            </span>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td><strong>{{ $t('total_hours') }}:</strong></td>
                                        <td>{{ selectedEmployee.total_hours }}h</td>
                                    </tr>
                                    <tr v-if="selectedEmployee.phone">
                                        <td><strong>{{ $t('phone') }}:</strong></td>
                                        <td>
                                            <a :href="'tel:' + selectedEmployee.phone" class="text-primary">
                                                <i class="fas fa-phone mr-1"></i>
                                                {{ selectedEmployee.phone }}
                                            </a>
                                        </td>
                                    </tr>
                                    <tr v-if="selectedEmployee.email">
                                        <td><strong>{{ $t('email') }}:</strong></td>
                                        <td>
                                            <a :href="'mailto:' + selectedEmployee.email" class="text-primary">
                                                <i class="fas fa-envelope mr-1"></i>
                                                {{ selectedEmployee.email }}
                                            </a>
                                        </td>
                                    </tr>
                                    <tr v-if="selectedEmployee.location">
                                        <td><strong>{{ $t('location') }}:</strong></td>
                                        <td>
                                            <span class="text-info">
                                                <i class="fas fa-map-marker-alt mr-1"></i>
                                                {{ selectedEmployee.location }}
                                            </span>
                                        </td>
                                    </tr>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import CoreLibrary from '../../../../core/helpers/CoreLibrary';
import AppFunction from '../../../../core/helpers/app/AppFunction';
import { ATTENDANCE_DAILY_EMPLOYEE_DETAILS, ATTENDANCE_EXPORT_SUMMARY } from '../../../Config/ApiUrl';

export default {
    extends: CoreLibrary,
    name: "EmployeeAttendanceDetails",
    data() {
        return {
            selectedDate: window.moment ? window.moment().format('YYYY-MM-DD') : new Date().toISOString().split('T')[0],
            viewMode: 'grid', // 'grid' or 'table'
            employeeDetails: [],
            filteredEmployeeDetails: [],
            searchQuery: '',
            selectedEmployee: null,
            statistics: {},
            tableId: 'employee-attendance-details-table',
            options: {
                name: 'EmployeeAttendanceDetails',
                url: ATTENDANCE_DAILY_EMPLOYEE_DETAILS,
                showHeader: true,
                enableHighlights: true,
                enableRowSelect: false,
                columns: [
                    {
                        title: this.$t('photo'),
                        type: 'media-object',
                        key: 'photo_url',
                        default: AppFunction.getAppUrl('images/avatar-demo.jpg'),
                        modifier: (value) => value || AppFunction.getAppUrl('images/avatar-demo.jpg')
                    },
                    {
                        title: this.$t('matricule'),
                        type: 'text',
                        key: 'matricule'
                    },
                    {
                        title: this.$t('employee'),
                        type: 'text',
                        key: 'full_name'
                    },
                    {
                        title: this.$t('department'),
                        type: 'text',
                        key: 'department'
                    },
                    {
                        title: this.$t('status'),
                        type: 'custom-html',
                        key: 'status',
                        modifier: (value) => {
                            const statusClasses = {
                                'present': 'badge-success',
                                'absent': 'badge-danger',
                                'completed': 'badge-primary',
                                'partial': 'badge-warning'
                            };
                            return `<span class="badge ${statusClasses[value] || 'badge-secondary'}">${this.$t(value)}</span>`;
                        }
                    },
                    {
                        title: this.$t('first_check_in'),
                        type: 'text',
                        key: 'first_check_in'
                    },
                    {
                        title: this.$t('last_check_out'),
                        type: 'text',
                        key: 'last_check_out'
                    },
                    {
                        title: this.$t('total_hours'),
                        type: 'custom-html',
                        key: 'total_hours',
                        modifier: (value) => `${value}h`
                    },
                    {
                        title: this.$t('indicators'),
                        type: 'custom-html',
                        key: 'is_late',
                        modifier: (value, row) => {
                            let indicators = [];
                            if (row.is_late) indicators.push('<span class="badge badge-warning">Retard</span>');
                            if (row.is_early_departure) indicators.push('<span class="badge badge-info">Départ anticipé</span>');
                            if (row.overtime_minutes > 0) indicators.push('<span class="badge badge-primary">Heures sup.</span>');
                            return indicators.length > 0 ? indicators.join(' ') : '-';
                        }
                    },
                    {
                        title: this.$t('actions'),
                        type: 'action'
                    }
                ],
                actions: [
                    {
                        title: this.$t('view_details'),
                        icon: 'eye',
                        type: 'view',
                        modifier: () => true
                    }
                ],
                filters: [
                    {
                        title: this.$t('status'),
                        type: "dropdown",
                        key: "status",
                        option: [
                            { id: 'present', value: this.$t('present') },
                            { id: 'absent', value: this.$t('absent') },
                            { id: 'completed', value: this.$t('completed') },
                            { id: 'partial', value: this.$t('partial') }
                        ]
                    },
                    {
                        title: this.$t('department'),
                        type: "multi-select-filter",
                        key: "departments",
                        option: []
                    }
                ],
                showFilter: true,
                showSearch: true,
                paginationType: "pagination",
                responsive: true,
                rowLimit: 15,
                showAction: true,
                orderBy: 'desc'
            }
        }
    },
    mounted() {
        this.loadEmployeeDetails();
    },
    methods: {
        async loadEmployeeDetails() {
            try {
                this.preLoader = true;
                const response = await this.axiosGet(`${ATTENDANCE_DAILY_EMPLOYEE_DETAILS}?date=${this.selectedDate}`);
                console.log('REPONSE', response);
                this.employeeDetails = response.data.employees;
                this.filteredEmployeeDetails = [...this.employeeDetails];
                this.filterEmployees(); // Apply any existing search filter
                this.calculateStatistics();
                
                // Update table URL and reload
                this.options.url = `${ATTENDANCE_DAILY_EMPLOYEE_DETAILS}?date=${this.selectedDate}`;
                this.$hub.$emit('reload-' + this.tableId);
                
            } catch (error) {
                console.error(error)
                this.$toastr.e(this.$t('error_loading_employee_details'));
            } finally {
                this.preLoader = false;
            }
        },

        filterEmployees() {
            if (!this.searchQuery || this.searchQuery.trim() === '') {
                this.filteredEmployeeDetails = [...this.employeeDetails];
            } else {
                const query = this.searchQuery.toLowerCase().trim();
                this.filteredEmployeeDetails = this.employeeDetails.filter(employee => {
                    const fullName = (employee.full_name || '').toLowerCase();
                    const matricule = (employee.matricule || '').toLowerCase();
                    const phone = (employee.phone || '').toLowerCase();
                    
                    return fullName.includes(query) || 
                           matricule.includes(query) || 
                           phone.includes(query);
                });
            }
            // Recalculate statistics based on filtered results
            this.calculateFilteredStatistics();
        },

        clearSearch() {
            this.searchQuery = '';
            this.filterEmployees();
        },

        calculateStatistics() {
            this.statistics = {
                present_count: this.employeeDetails.filter(emp => emp.status === 'present' || emp.status === 'completed').length,
                absent_count: this.employeeDetails.filter(emp => emp.status === 'absent').length,
                late_count: this.employeeDetails.filter(emp => emp.is_late).length,
                overtime_count: this.employeeDetails.filter(emp => emp.overtime_minutes > 0).length
            };
        },

        calculateFilteredStatistics() {
            // Update statistics based on filtered results for better UX
            this.statistics = {
                present_count: this.filteredEmployeeDetails.filter(emp => emp.status === 'present' || emp.status === 'completed').length,
                absent_count: this.filteredEmployeeDetails.filter(emp => emp.status === 'absent').length,
                late_count: this.filteredEmployeeDetails.filter(emp => emp.is_late).length,
                overtime_count: this.filteredEmployeeDetails.filter(emp => emp.overtime_minutes > 0).length
            };
        },

        toggleView() {
            this.viewMode = this.viewMode === 'grid' ? 'table' : 'grid';
        },

        reloadTable() {
            this.loadEmployeeDetails();
        },

        getCardClass(status) {
            const classes = {
                'present': 'border-success',
                'absent': 'border-danger',
                'completed': 'border-primary',
                'partial': 'border-warning'
            };
            return classes[status] || 'border-secondary';
        },

        getStatusBadgeClass(status) {
            const classes = {
                'present': 'badge-success',
                'absent': 'badge-danger',
                'completed': 'badge-primary',
                'partial': 'badge-warning'
            };
            return classes[status] || 'badge-secondary';
        },

        getDefaultPhoto() {
            return AppFunction.getAppUrl('images/avatar-demo.jpg');
        },

        getListAction(rowData, actionObj) {
            if (actionObj.type === 'view') {
                this.selectedEmployee = rowData;
                $('#employee-detail-modal').modal('show');
            }
        },

        exportDetails() {
            const url = `${ATTENDANCE_EXPORT_SUMMARY}?date=${this.selectedDate}`;
            window.open(url, '_blank');
        },

        // Helper method for moment
        moment(date) {
            return window.moment ? window.moment(date) : new Date(date);
        }
    }
}
</script>

<style scoped>
.employee-card {
    transition: transform var(--mozali-motion-micro) var(--mozali-motion-ease),
    box-shadow var(--mozali-motion-standard) var(--mozali-motion-ease);
    border-left-width: 4px;
    border-radius: var(--mozali-radius-lg);
    box-shadow: var(--mozali-shadow-card);
}

.employee-card:hover {
    transform: translateY(-4px);
    box-shadow: var(--mozali-shadow-card-hover);
}

.employee-avatar {
    flex-shrink: 0;
}

.employee-details small {
    font-size: 0.75rem;
}

.badge {
    font-size: 0.7rem;
}

.card {
    border-radius: var(--mozali-radius-lg);
}

.attendance-stat-card {
    border: none;
}

.attendance-stat-card--brand {
    background: var(--mozali-brand-gradient);
}

.attendance-stat-card--cyan {
    background: linear-gradient(135deg, var(--mozali-color-brand-dark) 0%, var(--mozali-color-cyan) 100%);
}

.attendance-stat-card--indigo {
    background: linear-gradient(135deg, var(--mozali-color-indigo) 0%, var(--mozali-color-brand-dark) 100%);
}

.attendance-stat-card--dark {
    background: var(--mozali-dark-gradient);
}

.attendance-date-input {
    width: auto;
    min-width: 190px;
}

.attendance-search-group {
    width: 250px;
}

.attendance-indicators-section {
    min-height: 30px;
}

.input-group {
    box-shadow: var(--mozali-shadow-card);
    border-radius: var(--mozali-radius-md);
}

.input-group .form-control {
    border: 1px solid var(--default-border-color);
}

.input-group .input-group-text {
    background-color: var(--base-color);
    border: 1px solid var(--default-border-color);
    color: var(--mozali-color-gray-600);
}

.input-group .btn-outline-secondary {
    border-color: var(--default-border-color);
    color: var(--mozali-color-gray-600);
}

.input-group .btn-outline-secondary:hover {
    background-color: var(--base-color);
    border-color: var(--mozali-color-gray-200);
}

/* Contact links styling */
.contact-link {
    text-decoration: none;
    font-size: 0.85rem;
    transition: color var(--mozali-motion-micro) var(--mozali-motion-ease);
}

.contact-link:hover {
    text-decoration: underline;
    color: var(--mozali-color-brand) !important;
}

.email-link {
    font-size: 0.8rem;
    word-break: break-all;
}

.location-text {
    font-size: 0.85rem;
    font-weight: 500;
}

.location-text i {
    color: var(--mozali-color-brand-dark);
}

@media (prefers-reduced-motion: reduce) {
    .employee-card,
    .contact-link {
        transition: none !important;
    }

    .employee-card:hover {
        transform: none !important;
    }
}
</style>
